import { useState } from 'react';
import { Attachment } from '../../../types';
import { ServiceRequest } from '../../../Api/ServiceRequest/serviceRequestApi';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { addServiceRequest } from '../../../store/ServiceRequest';
import { isPendingUpload, refusedFile, uploadStagedAttachments } from '../../../store/StagedAttachment';
import {
  hasOrphanedAttachments,
  hasUnsentAttachments,
  RequestSnapshot,
  SubmitOutcome,
  submitOutcomeMessage
} from './serviceRequestModel';

interface RequestSubmitInput {
  requests: RequestSnapshot[];
  activeTab: number;
  captureSnapshot: () => RequestSnapshot;
  revealTab: (snapshots: RequestSnapshot[], index: number) => void;
  buildRequest: (snapshot: RequestSnapshot) => ServiceRequest;
  accountId: string | undefined;
  accountSelectionMissing: boolean;
  onRequestsChange: (requests: RequestSnapshot[]) => void;
  onUploadProgress: (attachment: Attachment) => void;
  onSent: () => void;
}

/**
 * Submits every request tab in order, then uploads each tab's staged files to the Opportunity its submission
 * created. A tab that already succeeded is not sent again, so a retry resends only the failed ones; when anything
 * fails the form stays open on the first failed tab, or else on the first tab with a file still unsent.
 */
export function useRequestSubmit({
  requests,
  activeTab,
  captureSnapshot,
  revealTab,
  buildRequest,
  accountId,
  accountSelectionMissing,
  onRequestsChange,
  onUploadProgress,
  onSent
}: RequestSubmitInput) {
  const [disabled, setDisabled] = useState(false);
  const { setSnack } = useSnackbar();

  // The Opportunity a file hangs off is created by the submission, so uploads can only run after it.
  const uploadAttachmentsFor = (snapshot: RequestSnapshot, tabIndex: number): Promise<Attachment[]> =>
    uploadStagedAttachments(snapshot.attachments, snapshot.opportunityId ?? '', (updated) => {
      if (tabIndex === activeTab) onUploadProgress(updated);
    });

  const submitRequest = async (snapshot: RequestSnapshot, tabIndex: number): Promise<RequestSnapshot> => {
    let request = snapshot;

    if (request.status !== 'success') {
      const result = await addServiceRequest(buildRequest(request), accountId);

      if (!result) return { ...request, status: 'failed' };

      request = { ...request, status: 'success', opportunityId: result.opportunity_id ?? null };
    }

    if (!request.attachments.some(isPendingUpload)) return request;

    return { ...request, attachments: await uploadAttachmentsFor(request, tabIndex) };
  };

  const handleSubmitAll = async (): Promise<void> => {
    if (accountSelectionMissing) return;
    setDisabled(true);

    const resultRequests = [...requests];
    resultRequests[activeTab] = captureSnapshot();

    for (let i = 0; i < resultRequests.length; i++) {
      resultRequests[i] = await submitRequest(resultRequests[i], i);
      onRequestsChange([...resultRequests]);
    }

    const outcome: SubmitOutcome = {
      failedRequests: resultRequests.filter((request) => request.status === 'failed').length,
      orphanedFiles: resultRequests.some(hasOrphanedAttachments),
      unsentFiles: resultRequests.some(hasUnsentAttachments),
      refusedFiles: resultRequests.some((request) => request.attachments.some(refusedFile))
    };

    if (!outcome.failedRequests && !outcome.orphanedFiles && !outcome.unsentFiles && !outcome.refusedFiles) {
      setSnack({ message: 'Your email has been sent successfully.', type: 'success', open: true });
      onSent();
      return;
    }

    setDisabled(false);
    setSnack({ message: submitOutcomeMessage(outcome), type: 'error', open: true });
    revealTab(
      resultRequests,
      outcome.failedRequests
        ? resultRequests.findIndex((request) => request.status === 'failed')
        : resultRequests.findIndex((request) => request.attachments.some((a) => isPendingUpload(a) || refusedFile(a)))
    );
  };

  return { disabled, resetSubmit: () => setDisabled(false), handleSubmitAll };
}
